var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
// Accessibility https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Tab_Role
import * as React from 'react';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import toArray from 'rc-util/lib/Children/toArray';
import isMobile from 'rc-util/lib/isMobile';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import TabNavList from './TabNavList';
import TabPanelList from './TabPanelList';
import TabPane from './TabPanelList/TabPane';
import TabContext from './TabContext';
/**
 * Should added antd:
 * - type
 *
 * Removed:
 * - onNextClick
 * - onPrevClick
 * - keyboard
 */
// Used for accessibility
let uuid = 0;
function parseTabList(children) {
    return toArray(children)
        .map((node) => {
        if (React.isValidElement(node)) {
            const key = node.key !== undefined ? String(node.key) : undefined;
            return Object.assign(Object.assign({ key }, node.props), { node });
        }
        return null;
    })
        .filter(tab => tab);
}
function Tabs(_a, ref) {
    var { id, prefixCls = 'rc-tabs', className, children, direction, activeKey, defaultActiveKey, editable, animated = {
        inkBar: true,
        tabPane: false,
    }, tabPosition = 'top', tabBarGutter, tabBarStyle, tabBarExtraContent, locale, moreTransitionName, destroyInactiveTabPane, renderTabBar, onChange, onTabClick, onTabScroll, onEditTabNav } = _a, restProps = __rest(_a, ["id", "prefixCls", "className", "children", "direction", "activeKey", "defaultActiveKey", "editable", "animated", "tabPosition", "tabBarGutter", "tabBarStyle", "tabBarExtraContent", "locale", "moreTransitionName", "destroyInactiveTabPane", "renderTabBar", "onChange", "onTabClick", "onTabScroll", "onEditTabNav"]);
    const tabs = parseTabList(children);
    const rtl = direction === 'rtl';
    let mergedAnimated;
    if (animated === false) {
        mergedAnimated = {
            inkBar: false,
            tabPane: false,
        };
    }
    else if (animated === true) {
        mergedAnimated = {
            inkBar: true,
            tabPane: true,
        };
    }
    else {
        mergedAnimated = Object.assign({ inkBar: true, tabPane: false }, (typeof animated === 'object' ? animated : {}));
    }
    // ======================== Mobile ========================
    const [mobile, setMobile] = useState(false);
    useEffect(() => {
        // Only update on the client side
        setMobile(isMobile());
    }, []);
    // ====================== Active Key ======================
    const [mergedActiveKey, setMergedActiveKey] = useMergedState(() => { var _a; return (_a = tabs[0]) === null || _a === void 0 ? void 0 : _a.key; }, {
        value: activeKey,
        defaultValue: defaultActiveKey,
    });
    const [activeIndex, setActiveIndex] = useState(() => tabs.findIndex(tab => tab.key === mergedActiveKey));
    // Reset active key if not exist anymore
    useEffect(() => {
        var _a;
        let newActiveIndex = tabs.findIndex(tab => tab.key === mergedActiveKey);
        if (newActiveIndex === -1) {
            newActiveIndex = Math.max(0, Math.min(activeIndex, tabs.length - 1));
            setMergedActiveKey((_a = tabs[newActiveIndex]) === null || _a === void 0 ? void 0 : _a.key);
        }
        setActiveIndex(newActiveIndex);
    }, [tabs.map(tab => tab.key).join('_'), mergedActiveKey, activeIndex]);
    // ===================== Accessibility ====================
    const [mergedId, setMergedId] = useMergedState(null, {
        value: id,
    });
    let mergedTabPosition = tabPosition;
    if (mobile && !['left', 'right'].includes(tabPosition)) {
        mergedTabPosition = 'top';
    }
    // Async generate id to avoid ssr mapping failed
    useEffect(() => {
        if (!id) {
            setMergedId(`rc-tabs-${process.env.NODE_ENV === 'test' ? 'test' : uuid}`);
            uuid += 1;
        }
    }, []);
    // ======================== Events ========================
    function onInternalTabClick(key, e) {
        onTabClick === null || onTabClick === void 0 ? void 0 : onTabClick(key, e);
        setMergedActiveKey(key);
        onChange === null || onChange === void 0 ? void 0 : onChange(key);
    }
    // ======================== Render ========================
    const sharedProps = {
        id: mergedId,
        activeKey: mergedActiveKey,
        animated: mergedAnimated,
        tabPosition: mergedTabPosition,
        rtl,
        mobile,
    };
    let tabNavBar;
    const tabNavBarProps = Object.assign(Object.assign({}, sharedProps), { editable,
        locale,
        moreTransitionName,
        tabBarGutter, onTabClick: onInternalTabClick, onTabScroll,
        onEditTabNav, extra: tabBarExtraContent, style: tabBarStyle, panes: children });
    if (renderTabBar) {
        tabNavBar = renderTabBar(tabNavBarProps, TabNavList);
    }
    else {
        tabNavBar = <TabNavList {...tabNavBarProps}/>;
    }
    return (<TabContext.Provider value={{ tabs, prefixCls }}>
            <div ref={ref} id={id} className={classNames(prefixCls, `${prefixCls}-${mergedTabPosition}`, {
            [`${prefixCls}-mobile`]: mobile,
            [`${prefixCls}-editable`]: editable,
            [`${prefixCls}-rtl`]: rtl,
        }, className)} {...restProps}>
                {tabNavBar}
                <TabPanelList destroyInactiveTabPane={destroyInactiveTabPane} {...sharedProps} animated={mergedAnimated}/>
            </div>
        </TabContext.Provider>);
}
const ForwardTabs = React.forwardRef(Tabs);
ForwardTabs.TabPane = TabPane;
export default ForwardTabs;
